import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useDIDMetrics, useDIDs } from '@/features/dids/hooks';
import { useDIDStore } from '@/features/dids/stores/did-store';
import { DIDDetailModal } from '@/features/dids/components/did-detail-modal';
import { RemoveE911Modal } from '@/features/dids/components/remove-e911-modal';
import { formatPhoneNumber } from '@/lib/utils';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import { Check, X, LoaderCircle, Pencil, Trash2 } from 'lucide-react';

export function E911Page() {
  const { data: metrics } = useDIDMetrics();
  const { data: allDids, isLoading: didsLoading } = useDIDs();
  const { openDetailModal } = useDIDStore();

  // State for Remove E911 modal
  const [removeE911DID, setRemoveE911DID] = useState<string | null>(null);

  // Filter DIDs that have E911 registered
  const e911Dids = allDids?.filter((did) => did.has_911) || [];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-display font-semibold text-slate-800">
        Emergency 911 Dashboard
      </h1>

      {/* 911 Summary + My 911 DIDs side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* 911 Summary - 1 part */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>911 Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-2">
              <div className="flex justify-between">
                <dt className="text-sm text-slate-600">Registered</dt>
                <dd className="text-sm font-medium text-slate-800">
                  {metrics?.e911_registered ?? 0} DIDs
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-slate-600">Unregistered</dt>
                <dd className="text-sm font-medium text-slate-800">
                  {metrics?.e911_unregistered ?? 0} DIDs
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-slate-600">Off-Network</dt>
                <dd className="text-sm font-medium text-slate-800">
                  {metrics?.e911_off_network ?? 0} DIDs
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-slate-600">w/ Alerts Configured</dt>
                <dd className="text-sm font-medium text-slate-800">
                  {metrics?.e911_with_alerts ?? 0} DIDs
                </dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        {/* My 911 DIDs Table - 3 parts */}
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle>My 911 DIDs</CardTitle>
          </CardHeader>
          <CardContent>
          {didsLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoaderCircle className="h-6 w-6 animate-spin text-slate-400" />
              <span className="ml-2 text-sm text-slate-500">Loading DIDs...</span>
            </div>
          ) : e911Dids.length === 0 ? (
            <p className="text-sm text-slate-500 italic py-4">
              No DIDs with E911 registered
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-36">Phone Number</TableHead>
                  <TableHead className="w-16 text-center">911</TableHead>
                  <TableHead className="w-16 text-center">Alerts</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {e911Dids.map((did) => (
                  <TableRow key={did.tn}>
                    <TableCell className="font-medium whitespace-nowrap">
                      {formatPhoneNumber(did.tn)}
                    </TableCell>
                    <TableCell className="text-center">
                      {did.has_911 ? (
                        <Check className="h-4 w-4 text-green-600 mx-auto" />
                      ) : (
                        <X className="h-4 w-4 text-red-500 mx-auto" />
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {did.e911_has_alerts ? (
                        <Check className="h-4 w-4 text-green-600 mx-auto" />
                      ) : (
                        <X className="h-4 w-4 text-red-500 mx-auto" />
                      )}
                    </TableCell>
                    <TableCell>
                      {did.e911_address1 ? (
                        <span className="text-sm">
                          {did.e911_address1}
                          {did.e911_address2 && `, ${did.e911_address2}`}
                          {did.e911_city && `, ${did.e911_city}`}
                          {did.e911_state && `, ${did.e911_state}`}
                          {did.e911_zip && ` ${did.e911_zip}`}
                        </span>
                      ) : (
                        <span className="text-slate-400 italic">No address</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openDetailModal(did.tn, 'e911')}
                          className="p-2 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                          aria-label="Edit E911"
                          title="Edit E911 Address"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setRemoveE911DID(did.tn)}
                          className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          aria-label="Remove E911"
                          title="Remove E911 Registration"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      </div>

      {/* Modals */}
      <DIDDetailModal />
      <RemoveE911Modal
        tn={removeE911DID}
        isOpen={removeE911DID !== null}
        onClose={() => setRemoveE911DID(null)}
      />
    </div>
  );
}
